import React, { useState } from 'react'
import clsx from 'clsx'
import moment from 'moment'
import 'moment/locale/es'
import { patchToApi } from 'actions'
import { useApiRequest } from 'utils/customHooks/useApiRequest'
import {
   makeStyles,
   Grid,
   Typography,
   Collapse,
   Divider,
   Paper
} from '@material-ui/core'
import SmsIcon from '@material-ui/icons/Sms'
import PriorityHighIcon from '@material-ui/icons/PriorityHigh'
import { ExpandLess, ExpandMore } from '@material-ui/icons'
import MaterialIcon from '../materialIcon/materialIcon'

const useNotificationCardStyles = makeStyles(theme => ({
   cardContainer: {
      marginBottom: theme.spacing(1)
   },
   clickable: {
      cursor: 'pointer'
   },
   cardHeader: {
      backgroundColor: '#EBEFF8',
      display: 'flex',
      justifyContent: 'space-between',
      padding: theme.spacing(1)
   },
   cardHeaderSide: {
      display: 'flex'
   },
   cardHeaderTitleAndIconContainer: {
      display: 'flex',
      alignItems: 'center'
   },
   cardHeaderTitle: {
      fontSize: 10,
      color: '#8A8FAB'
   },
   cardHeaderTitleAndIcon: {
      marginLeft: theme.spacing(0.5)
   },
   cardHeaderPriority: {
      paddingLeft: theme.spacing(1)
   },
   high: {
      color: '#F01E46'
   },
   medium: {
      color: '#F08E1E'
   },
   low: {
      color: '#60DC32'
   },
   dot: {
      height: 6,
      width: 6,
      marginLeft: theme.spacing(1),
      marginTop: 2,
      borderRadius: '50%',
      display: 'inline-block'
   },
   read: {
      backgroundColor: '#3264DC'
   },
   unread: {
      backgroundColor: '#60DC32'
   },
   cardBody: {
      display: 'flex',
      padding: theme.spacing(1.5)
   },
   cardBodyIconContainer: {
      color: '#3264DC',
      marginRight: theme.spacing(1.5)
   },
   cardHeaderPriorityIconContainer: {
      fontSize: 12
   },
   cardBodyCommentContainer: {
      flex: '1 1 auto'
   },
   cardBodyComment: {
      textAlign: 'left',
      fontSize: 13,
      color: '#43496A'
   },
   collapseContainer: {
      height: 40
   }
}))

const NotificationCard = ({ notification, isCollapsable = false }) => {
   const classes = useNotificationCardStyles()

   const { id, application, hour, createdDate, priority, read, comment } =
      notification
   const {
      name: applicationName,
      subdomain: { name: subdomainName, icon: subdomainIcon }
   } = application

   const [isOpen, setIsOpen] = useState(false)
   const [isRead, setIsRead] = useState(read)

   async function _updateReadStatus(notificationId) {
      const payload = { read: true }
      const url = `${process.env.REACT_APP_NOTIFICATIONS_API}/notifications/${notificationId}`
      await patchToApi(url, payload)
      setIsRead(true)
   }

   const updateReadStatus = useApiRequest(
      _updateReadStatus,
      'Notificación Card: Marcar leída',
      false
   )

   const localLocale = moment(
      `${createdDate} ${hour}`,
      'DD-MM-YYYY hh:mm:ss'
   ).locale('es')
   const prettyCreatedDate = localLocale.fromNow()
   const formattedHour = localLocale.format('h:mm a')

   function toggleOpen() {
      if (!isOpen && !isRead) {
         updateReadStatus(id)
      }
      setIsOpen(previous => !previous)
   }

   function handleClick() {
      if (!isCollapsable && !isRead) {
         updateReadStatus(id)
      }
   }

   const Component = isCollapsable ? Grid : Paper

   return (
      <Component
         className={clsx({
            [classes.cardContainer]: !isCollapsable,
            [classes.clickable]: !isRead && !isCollapsable
         })}
         onClick={handleClick}
      >
         <Grid className={classes.cardHeader}>
            <Grid className={classes.cardHeaderSide}>
               <Grid>
                  <Grid className={classes.cardHeaderTitleAndIconContainer}>
                     <MaterialIcon
                        fill="outlined"
                        name={subdomainIcon}
                        size={12}
                     />
                     <Typography
                        className={`${classes.cardHeaderTitle} ${classes.cardHeaderTitleAndIcon}`}
                     >
                        {subdomainName}
                     </Typography>
                  </Grid>
                  <Typography className={classes.cardHeaderTitle}>
                     {applicationName}
                  </Typography>
                  <Typography className={classes.cardHeaderTitle}>
                     {prettyCreatedDate}
                  </Typography>
               </Grid>
               <Grid
                  className={`${classes[priority.toLowerCase()]} ${
                     classes.cardHeaderPriority
                  }`}
               >
                  <PriorityHighIcon fontSize="inherit" />
               </Grid>
            </Grid>
            <Grid className={classes.cardHeaderSide}>
               <Typography className={classes.cardHeaderTitle}>
                  {formattedHour}
               </Typography>
               <span
                  className={clsx(classes.dot, {
                     [classes.read]: isRead,
                     [classes.unread]: !isRead
                  })}
               />
            </Grid>
         </Grid>
         {isCollapsable ? (
            <CollapsableCardBody
               comment={comment}
               isOpen={isOpen}
               toggleOpen={toggleOpen}
               isRead={isRead}
            />
         ) : (
            <CardBody comment={comment} />
         )}
      </Component>
   )
}

export default NotificationCard

// -----------------------

function CollapsableCardBody({ comment, isOpen, isRead, toggleOpen }) {
   const classes = useNotificationCardStyles()

   return (
      <Collapse in={isOpen} collapsedSize={50}>
         <Grid
            className={clsx(classes.cardBody, {
               [classes.clickable]: !isRead
            })}
            onClick={toggleOpen}
         >
            <Grid className={classes.cardBodyIconContainer}>
               <SmsIcon fontSize="small" />
            </Grid>
            <Grid className={classes.cardBodyCommentContainer}>
               <Typography className={classes.cardBodyComment}>
                  {comment}
               </Typography>
            </Grid>
            <Grid>{isOpen ? <ExpandLess /> : <ExpandMore />}</Grid>
         </Grid>
         <Divider variant="middle" />
      </Collapse>
   )
}

// -----------------------

function CardBody({ comment }) {
   const classes = useNotificationCardStyles()

   return (
      <Grid className={classes.cardBody}>
         <Grid className={classes.cardBodyIconContainer}>
            <SmsIcon fontSize="small" />
         </Grid>
         <Grid className={classes.cardBodyCommentContainer}>
            <Typography className={classes.cardBodyComment}>
               {comment}
            </Typography>
         </Grid>
      </Grid>
   )
}
